import { useSelector } from 'react-redux'
import { useLocation, useParams } from 'react-router-dom'

export const filterCurrentOrgExperiences = (positions, currentOrg) =>
	positions.filter((position) => {
		if (position.orgId) return position.orgId === currentOrg?.orgId
		if (position.orgSlug) return position.orgSlug === currentOrg?.orgSlug
		return true
	})

// Route identity takes precedence over persisted selections while context loads.
export const resolveWorkspaceContext = ({
	pathname = '/',
	orgSlug,
	experienceSlug,
	currentOrg,
	currentExperience,
	orgMemberships = [],
	orgDetails,
	experienceDetails,
	experiencePositions = [],
}) => {
	const hasSwitchableOrg = orgMemberships.some(
		(org) => org.isActive !== false && org.orgSlug !== currentOrg?.orgSlug,
	)
	const positions = filterCurrentOrgExperiences(experiencePositions, currentOrg)
	const showExperienceSwitcher =
		positions.length > 1 ||
		positions.some((position) => position.experienceStatus === 'inactive')

	let organizationName
	let scopeName
	let scopeKind

	if (/^\/control(?:\/|$)/.test(pathname)) {
		organizationName = 'LiveHQ'
		scopeName = 'Control workspace'
		scopeKind = 'control'
	} else if (/^\/account(?:\/|$)/.test(pathname)) {
		organizationName = 'LiveHQ'
		scopeName = 'Account settings'
		scopeKind = 'account'
	} else {
		const selectedOrg =
			!orgSlug || currentOrg?.orgSlug === orgSlug ? currentOrg : null
		const routeOrgDetails = orgDetails?.slug === orgSlug ? orgDetails : null
		const membership = orgMemberships.find((org) => org.orgSlug === orgSlug)
		const routeOrgId =
			selectedOrg?.orgId || routeOrgDetails?.id || membership?.orgId

		organizationName =
			selectedOrg?.orgName ||
			routeOrgDetails?.name ||
			membership?.orgName ||
			'Organization'
		scopeKind = experienceSlug ? 'experience' : 'organization'
		scopeName = 'Organization Workspace'

		if (experienceSlug) {
			const matchingDetails =
				experienceDetails?.slug === experienceSlug &&
				routeOrgId &&
				experienceDetails?.orgId === routeOrgId
			const matchingSelection =
				orgSlug &&
				currentOrg?.orgSlug === orgSlug &&
				currentExperience?.slug === experienceSlug
			scopeName =
				(matchingDetails && experienceDetails.name) ||
				(matchingSelection && currentExperience.name) ||
				'Experience'
		}
	}

	return {
		organizationName,
		scopeName,
		scopeKind,
		hasSwitchableOrg,
		showExperienceSwitcher,
	}
}

export default function useWorkspaceContext() {
	const { orgSlug, experienceSlug } = useParams()
	const { pathname } = useLocation()
	const currentOrg = useSelector((state) => state.user.currentOrg)
	const currentExperience = useSelector((state) => state.user.currentExperience)
	const orgMemberships = useSelector((state) => state.user.orgMemberships)
	const orgDetails = useSelector((state) => state.org.orgDetails)
	const experiencePositions = useSelector((state) => state.org.userPositions)
	const experienceDetails = useSelector(
		(state) => state.experience.experienceDetails,
	)

	return resolveWorkspaceContext({
		pathname,
		orgSlug,
		experienceSlug,
		currentOrg,
		currentExperience,
		orgMemberships: orgMemberships || [],
		orgDetails,
		experienceDetails,
		experiencePositions: experiencePositions || [],
	})
}
